/**
 * DelaysSection Component
 * Dynamic delay entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { AlertTriangle, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { DelayEntry, DelayType } from '../../../types/dailyLog';
import { Dropdown, Input, NumberInput, TextArea } from '../../forms';

// Delay type options
const DELAY_TYPE_OPTIONS: { value: DelayType; label: string }[] = [
  { value: 'weather', label: 'Weather' },
  { value: 'material', label: 'Material Shortage' },
  { value: 'equipment', label: 'Equipment Failure' },
  { value: 'labor', label: 'Labor Shortage' },
  { value: 'inspection', label: 'Inspection Delay' },
  { value: 'design_change', label: 'Design Change' },
  { value: 'permit', label: 'Permit Issue' },
  { value: 'utility', label: 'Utility Work' },
  { value: 'subcontractor', label: 'Subcontractor Issue' },
  { value: 'owner', label: 'Owner Direction' },
  { value: 'other', label: 'Other' },
];

const DELAY_TYPE_COLORS: Record<DelayType, string> = {
  weather: 'bg-blue-100 text-blue-800',
  material: 'bg-orange-100 text-orange-800',
  equipment: 'bg-red-100 text-red-800',
  labor: 'bg-purple-100 text-purple-800',
  inspection: 'bg-yellow-100 text-yellow-800',
  design_change: 'bg-indigo-100 text-indigo-800',
  permit: 'bg-pink-100 text-pink-800',
  utility: 'bg-cyan-100 text-cyan-800',
  subcontractor: 'bg-amber-100 text-amber-800',
  owner: 'bg-emerald-100 text-emerald-800',
  other: 'bg-gray-100 text-gray-800',
};

// Common trades for multi-select datalist
const TRADE_OPTIONS = [
  'General Labor',
  'Carpenter',
  'Electrician',
  'Plumber',
  'HVAC',
  'Mason',
  'Iron Worker',
  'Concrete',
  'Painter',
  'Roofer',
  'Drywall',
  'All Trades',
];

interface DelaySectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'delays', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const DelaysSection: React.FC<DelaySectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const delayErrors = errors.delays as unknown as FieldErrors<DelayEntry>[] | undefined;

  const handleAddEntry = () => {
    append({
      type: 'other',
      description: '',
      hoursLost: 1,
      impactedTrades: [],
      responsibleParty: '',
      resolutionPlan: '',
      isResolved: false,
    });
  };

  // Calculate totals
  const totalDelayHours = fields.reduce((sum, _, index) => {
    const hours = (control._formValues?.delays?.[index]?.hoursLost as number) || 0;
    return sum + hours;
  }, 0);

  const unresolvedCount = fields.filter((_, index) => {
    return !(control._formValues?.delays?.[index]?.isResolved as boolean);
  }).length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <span className="font-medium text-gray-900">Delays & Issues</span>
          {fields.length > 0 && (
            <span className="text-sm text-gray-500">
              ({fields.length} {fields.length === 1 ? 'delay' : 'delays'})
            </span>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {fields.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-red-600 font-medium">
                {totalDelayHours.toFixed(1)} hours lost
              </span>
              {unresolvedCount > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                  {unresolvedCount} unresolved
                </span>
              )}
            </div>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => {
            const delayType = control._formValues?.delays?.[index]?.type as DelayType;
            const isResolved = control._formValues?.delays?.[index]?.isResolved as boolean;

            return (
              <div
                key={field.id}
                className={`p-4 border rounded-lg space-y-4 ${
                  isResolved ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                }`}
              >
                {/* Entry Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">Delay #{index + 1}</span>
                    {delayType && (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${DELAY_TYPE_COLORS[delayType]}`}
                      >
                        {DELAY_TYPE_OPTIONS.find(o => o.value === delayType)?.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Resolved Toggle */}
                    <Controller
                      name={`delays.${index}.isResolved`}
                      control={control}
                      render={({ field: inputField }) => (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inputField.value}
                            onChange={inputField.onChange}
                            disabled={disabled}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-600">Resolved</span>
                        </label>
                      )}
                    />
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                        title="Remove entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Type & Hours Lost */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Controller
                    name={`delays.${index}.type`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Dropdown
                        label="Delay Type"
                        options={DELAY_TYPE_OPTIONS}
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={delayErrors?.[index]?.type?.message}
                        disabled={disabled}
                        required
                      />
                    )}
                  />

                  <Controller
                    name={`delays.${index}.hoursLost`}
                    control={control}
                    render={({ field: inputField }) => (
                      <NumberInput
                        label="Hours Lost"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={delayErrors?.[index]?.hoursLost?.message}
                        disabled={disabled}
                        required
                        min={0.25}
                        max={24}
                        step={0.25}
                      />
                    )}
                  />

                  <Controller
                    name={`delays.${index}.responsibleParty`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        label="Responsible Party"
                        value={inputField.value || ''}
                        onChange={inputField.onChange}
                        error={delayErrors?.[index]?.responsibleParty?.message}
                        disabled={disabled}
                        placeholder="Who is responsible"
                      />
                    )}
                  />
                </div>

                {/* Description */}
                <Controller
                  name={`delays.${index}.description`}
                  control={control}
                  render={({ field: inputField }) => (
                    <TextArea
                      label="Description"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={delayErrors?.[index]?.description?.message}
                      disabled={disabled}
                      required
                      rows={2}
                      placeholder="Describe the delay and its cause..."
                    />
                  )}
                />

                {/* Impacted Trades */}
                <Controller
                  name={`delays.${index}.impactedTrades`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Impacted Trades
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(inputField.value || []).map((trade: string, tradeIndex: number) => (
                          <span
                            key={tradeIndex}
                            className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                          >
                            {trade}
                            {!disabled && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newTrades = [...inputField.value];
                                  newTrades.splice(tradeIndex, 1);
                                  inputField.onChange(newTrades);
                                }}
                                className="ml-1 text-gray-400 hover:text-red-500"
                              >
                                &times;
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {!disabled && (
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add trade..."
                            list={`impacted-trades-options-${index}`}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const target = e.target as HTMLInputElement;
                                const value = target.value.trim();
                                if (value && !inputField.value?.includes(value)) {
                                  inputField.onChange([...(inputField.value || []), value]);
                                  target.value = '';
                                }
                              }
                            }}
                          />
                          <datalist id={`impacted-trades-options-${index}`}>
                            {TRADE_OPTIONS.filter(trade => !inputField.value?.includes(trade)).map(
                              trade => (
                                <option key={trade} value={trade} />
                              )
                            )}
                          </datalist>
                        </div>
                      )}
                    </div>
                  )}
                />

                {/* Resolution Plan */}
                <Controller
                  name={`delays.${index}.resolutionPlan`}
                  control={control}
                  render={({ field: inputField }) => (
                    <TextArea
                      label="Resolution Plan"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={delayErrors?.[index]?.resolutionPlan?.message}
                      disabled={disabled}
                      rows={2}
                      placeholder="How will this delay be addressed..."
                    />
                  )}
                />
              </div>
            );
          })}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Report Delay</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              No delays reported
            </div>
          )}

          {/* No delays is good! */}
          {fields.length === 0 && !disabled && (
            <div className="text-center py-2 text-sm text-green-600">
              No delays reported - great job!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DelaysSection;
